import React from 'react';
import { gql, useQuery } from '@apollo/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { 
  AlertCircle, 
  MessageSquare, 
  Users, 
  Calendar, 
  Paperclip, 
  Download,
  Mail,
  User
} from 'lucide-react';

const GET_THREAD_MESSAGES = gql`
  query GetThreadMessages($threadId: String!) {
    getThreadMessages(threadId: $threadId) {
      messages {
        uniqueid
        threadId
        senderId
        senderString
        recipientId
        recipientString
        subject
        body
        createdAt
        attachments {
          filename
          mimeType
          data
        }
      }
      thread {
        uniqueid
        subject
        toJson
        ccJson
        replyJson
        tags
        createdAt
        messageCount
        lastMessageAt
      }
      participants
    }
  }
`;

interface ThreadDetailModalProps {
  threadId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const ThreadDetailModal: React.FC<ThreadDetailModalProps> = ({
  threadId,
  isOpen,
  onClose
}) => {
  const { data, loading, error } = useQuery(GET_THREAD_MESSAGES, {
    variables: { threadId },
    skip: !threadId,
    fetchPolicy: 'cache-first'
  });

  const downloadAttachment = (attachment: any) => {
    try {
      const binaryString = atob(attachment.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      const blob = new Blob([bytes], { type: attachment.mimeType });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
    }
  };

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Loading Thread Details...</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Error Loading Thread</DialogTitle>
          </DialogHeader>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to load thread details: {error.message}
            </AlertDescription>
          </Alert>
        </DialogContent>
      </Dialog>
    );
  }

  const { messages = [], thread, participants = [] } = data?.getThreadMessages || {};

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Thread: {thread?.subject || 'No Subject'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Thread Info Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Participants ({participants.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {participants.map((participant: string, index: number) => (
                  <div key={index} className="text-sm text-gray-600 flex items-center gap-2">
                    <User className="h-3 w-3" />
                    {participant}
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Thread Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <div className="text-xs text-gray-500">Thread ID</div>
                  <div className="text-sm font-mono">{thread?.uniqueid}</div>
                </div>
                
                <div>
                  <div className="text-xs text-gray-500">Created</div>
                  <div className="text-sm">{new Date(thread?.createdAt).toLocaleString()}</div>
                </div>
                
                <div>
                  <div className="text-xs text-gray-500">Messages</div>
                  <div className="text-sm font-medium">{thread?.messageCount}</div>
                </div>
                
                {thread?.tags && (
                  <div>
                    <div className="text-xs text-gray-500">Tags</div>
                    <Badge variant="secondary" className="text-xs mt-1">
                      {thread.tags}
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Messages */}
          <div className="lg:col-span-3">
            <ScrollArea className="h-[60vh]">
              <div className="space-y-4 pr-4">
                {messages.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No messages found in this thread
                  </div>
                ) : (
                  messages.map((message: any, index: number) => (
                    <div key={message.uniqueid}>
                      <Card>
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <Mail className="h-4 w-4 text-gray-400" />
                                <span className="font-medium">
                                  {message.senderString || `User ID: ${message.senderId}`}
                                </span>
                                <span className="text-sm text-gray-500">→</span>
                                <span className="text-sm text-gray-600">
                                  {message.recipientString || `User ID: ${message.recipientId}`}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <Calendar className="h-3 w-3" />
                                {new Date(message.createdAt).toLocaleString()}
                              </div>
                            </div>
                            
                            {message.attachments?.length > 0 && (
                              <Badge variant="secondary" className="flex items-center gap-1">
                                <Paperclip className="h-3 w-3" />
                                {message.attachments.length}
                              </Badge>
                            )}
                          </div>
                        </CardHeader>
                        
                        <CardContent className="space-y-4">
                          {/* Message Body */}
                          <div 
                            className="prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: message.body }}
                          />
                          
                          {/* Attachments */}
                          {message.attachments?.length > 0 && (
                            <div className="space-y-2">
                              <div className="text-sm font-medium flex items-center gap-2">
                                <Paperclip className="h-4 w-4" />
                                Attachments ({message.attachments.length})
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {message.attachments.map((attachment: any, attIndex: number) => (
                                  <div 
                                    key={attIndex}
                                    className="flex items-center justify-between p-2 bg-gray-50 rounded border"
                                  >
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-medium truncate">
                                        {attachment.filename}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {attachment.mimeType}
                                      </div>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => downloadAttachment(attachment)}
                                    >
                                      <Download className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                      
                      {index < messages.length - 1 && (
                        <Separator className="my-4" />
                      )}
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};